import { createSlice } from '@reduxjs/toolkit';
import { ReducerStatus } from 'utils/constants';
import {
  createLearningPlanAction,
  deleteLearningPlanAction,
  getLearningPlanAction,
  getLearningPlansAction,
  unbindClassFromLearningPlanAction,
  updateLearningPlanAction,
} from './learningPlan-action';
import updatePublishStatusAction from '../common/publishEntity/publishEntity-action';

const learningPlanSlice = createSlice({
  name: 'learningPlan',
  initialState: {
    learningPlans: [],
    learningPlan: null,
    learningPlansMetaData: null,
    getLearningPlans: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getLearningPlan: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    createLearningPlan: {
      modal: false,
      status: ReducerStatus.IDLE,
      error: null,
    },
    unbindClassFromLearningPlan: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    updateLearningPlan: {
      modal: false,
      id: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    deleteLearningPlan: {
      modal: false,
      id: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
  },
  reducers: {
    setCreateLearningPlan(state, action) {
      state.createLearningPlan = {
        ...state.createLearningPlan,
        ...action.payload,
      };
    },
    setUpdateLearningPlan(state, action) {
      state.updateLearningPlan = {
        ...state.updateLearningPlan,
        ...action.payload,
      };
    },
    setDeleteLearningPlan(state, action) {
      state.deleteLearningPlan = {
        ...state.deleteLearningPlan,
        ...action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Learning Plans
      .addCase(getLearningPlansAction.pending, (state) => {
        state.getLearningPlans.status = ReducerStatus.LOADING;
      })
      .addCase(getLearningPlansAction.fulfilled, (state, action) => {
        state.getLearningPlans.status = ReducerStatus.SUCCEEDED;
        const { metaData, data } = action.payload;

        state.learningPlans = data;
        state.learningPlansMetaData = metaData;
      })
      .addCase(getLearningPlansAction.rejected, (state, action) => {
        state.getLearningPlans.status = ReducerStatus.FAILED;
        state.getLearningPlans.error = action.payload;
      })
      // Get Learning Plan
      .addCase(getLearningPlanAction.pending, (state) => {
        state.getLearningPlan.status = ReducerStatus.LOADING;
      })
      .addCase(getLearningPlanAction.fulfilled, (state, action) => {
        state.getLearningPlan.status = ReducerStatus.SUCCEEDED;
        state.learningPlan = action.payload;
      })
      .addCase(getLearningPlanAction.rejected, (state, action) => {
        state.getLearningPlan.status = ReducerStatus.FAILED;
        state.getLearningPlan.error = action.payload;
      })
      // Create Learning Plan
      .addCase(createLearningPlanAction.pending, (state) => {
        state.createLearningPlan.status = ReducerStatus.LOADING;
      })
      .addCase(createLearningPlanAction.fulfilled, (state, action) => {
        state.createLearningPlan.status = ReducerStatus.SUCCEEDED;
        state.learningPlans = [action.payload, ...state.learningPlans];
        state.learningPlan = action.payload;
      })
      .addCase(createLearningPlanAction.rejected, (state, action) => {
        state.createLearningPlan.status = ReducerStatus.FAILED;
        state.createLearningPlan.error = action.payload;
      })
      // Unbind Class from Learning Plan
      .addCase(unbindClassFromLearningPlanAction.pending, (state) => {
        state.unbindClassFromLearningPlan.status = ReducerStatus.LOADING;
      })
      .addCase(unbindClassFromLearningPlanAction.fulfilled, (state, action) => {
        state.unbindClassFromLearningPlan.status = ReducerStatus.SUCCEEDED;
        state.learningPlan = action.payload;
      })
      .addCase(unbindClassFromLearningPlanAction.rejected, (state, action) => {
        state.unbindClassFromLearningPlan.status = ReducerStatus.FAILED;
        state.unbindClassFromLearningPlan.error = action.payload;
      })
      // Update Learning Plan
      .addCase(updateLearningPlanAction.pending, (state) => {
        state.updateLearningPlan.status = ReducerStatus.LOADING;
      })
      .addCase(updateLearningPlanAction.fulfilled, (state, action) => {
        state.updateLearningPlan.status = ReducerStatus.SUCCEEDED;
        const updatedLearningPlans = state.learningPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan
        );
        state.learningPlans = updatedLearningPlans;
        state.learningPlan = action.payload;
      })
      .addCase(updateLearningPlanAction.rejected, (state, action) => {
        state.updateLearningPlan.status = ReducerStatus.FAILED;
        state.updateLearningPlan.error = action.payload;
      })
      // Delete Learning Plan
      .addCase(deleteLearningPlanAction.pending, (state) => {
        state.deleteLearningPlan.status = ReducerStatus.LOADING;
      })
      // Update Publish Status
      .addCase(updatePublishStatusAction.fulfilled, (state, action) => {
        const { entityType, entityId, isPublished } = action.payload;

        if (entityType === 'LearningPlan') {
          if (state.learningPlan && state.learningPlan.id === entityId) {
            state.learningPlan.isPublished = isPublished;
          }
          state.learningPlans = state.learningPlans.map((plan) =>
            plan.id === entityId ? { ...plan, isPublished } : plan
          );
        }
      })
      .addCase(deleteLearningPlanAction.fulfilled, (state, action) => {
        state.deleteLearningPlan.status = ReducerStatus.SUCCEEDED;
        const filteredLearningPlans = state.learningPlans.filter(
          (plan) => plan.id !== action.payload
        );
        state.learningPlans = filteredLearningPlans;
      })
      .addCase(deleteLearningPlanAction.rejected, (state, action) => {
        state.deleteLearningPlan.status = ReducerStatus.FAILED;
        state.deleteLearningPlan.error = action.payload;
      });
  },
});

export const learningPlanActions = learningPlanSlice.actions;

export default learningPlanSlice;
